import { useMutation } from "@apollo/client";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";

import { UPDATE_IMAGE_ORDER as updateImageOrderMutation } from "../../../../graphql/mutation/Image";
import useSliderPosition from "../../../../hooks/useSliderPosition";
import useUser from "../../../../hooks/useUser";
import { Button, Modal } from "../../../ui";
import SortableThumbnail from "./components/SortableThumbnail";
import DeleteImage from "./components/DeleteImage";
import MoveImage from "./components/MoveImage";
import UploadImage from "./components/UploadImage";
import {
  clampThumbnailScale,
  DEFAULT_FULLSCREEN,
  DEFAULT_THUMBNAIL_SCALE,
  FULLSCREEN_OFF,
  FULLSCREEN_ON,
  FULLSCREEN_KEY,
  getModalScaleForFullscreen,
  getThumbnailSizeForScale,
  isImageOrderChanged,
  mergeIncomingImages,
  reorder,
  THUMBNAIL_SCALE_KEY,
  THUMBNAIL_SCALE_MAX,
  THUMBNAIL_SCALE_MIN,
  THUMBNAIL_SCALE_STEP,
} from "./utils";

const ManageImage = props => {
  const {
    images: propsImages = [],
    setImageList,
    setSuccessOpModal,
    moveAble = true,
    deleteAble = true,
    orderUpdateAble = true,
    artwork,
  } = props;

  const { hasPermission } = useUser();
  const [thumbnailScale, setThumbnailScale] = useSliderPosition(
    THUMBNAIL_SCALE_KEY,
    DEFAULT_THUMBNAIL_SCALE,
  );
  const [fullscreen, setFullscreen] = useSliderPosition(
    FULLSCREEN_KEY,
    DEFAULT_FULLSCREEN,
  );
  const isFullscreen = Number(fullscreen) === FULLSCREEN_ON;
  const [updateImageOrder, { loading: updateImageOrderLoading }] = useMutation(
    updateImageOrderMutation({ imgT: true, order: true }),
    { refetchQueries: ["FetchArtwork", "FetchInstance"] },
  );

  const [localImages, setLocalImages] = useState(propsImages);

  // The grid keeps its own list so a drag isn't lost on an unrelated render.
  // Adopt server updates (a finished upload, a new thumbnail) unless that
  // would wipe an unsaved reorder.
  useEffect(() => {
    setLocalImages(current => mergeIncomingImages(current, propsImages));
  }, [propsImages]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const enableUpdateOrderButton = useMemo(
    () => isImageOrderChanged(localImages, propsImages),
    [localImages, propsImages],
  );

  const canUpdateOrder = hasPermission("ORDER_IMAGES") && orderUpdateAble;
  // Only treat a reorder as "unsaved" when the user can actually persist it,
  // so we never offer a Save & exit action that can't succeed.
  const hasUnsavedReorder = canUpdateOrder && enableUpdateOrderButton;

  const resetLocalState = useCallback(() => {
    setSelectedImages([]);
    setLocalImages(propsImages);
    setShowUnsavedWarning(false);
  }, [propsImages]);

  const handleSelectImage = useCallback(
    archiveId => {
      if (!selectedImages.includes(archiveId)) {
        setSelectedImages(selected => [...selected, archiveId]);
      }
    },
    [selectedImages],
  );

  const handleUnselectImage = useCallback(
    archiveId => {
      if (selectedImages.includes(archiveId)) {
        setSelectedImages(selected => selected.filter(id => id !== archiveId));
      }
    },
    [selectedImages],
  );

  const removeUnLinkedImages = useCallback(() => {
    setImageList?.(prevState =>
      prevState.filter(item => !selectedImages.includes(item.archiveId)),
    );
    setLocalImages(prevState =>
      prevState.filter(item => !selectedImages.includes(item.archiveId)),
    );
  }, [selectedImages, setImageList]);

  const onDeleteSuccess = useCallback(() => {
    setSuccessOpModal?.({
      isOpen: true,
      message: `${selectedImages.length} ${selectedImages.length > 1 ? "images" : "image"} deleted successfully.`,
    });
    // Drop the deleted images from the visible list and clear the selection,
    // but keep the Manage Images modal open with the remaining images.
    removeUnLinkedImages();
    setSelectedImages([]);
  }, [removeUnLinkedImages, selectedImages.length, setSuccessOpModal]);

  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    setLocalImages(images => {
      const fromIndex = images.findIndex(item => item.archiveId === active.id);
      const toIndex = images.findIndex(item => item.archiveId === over.id);
      return reorder(images, fromIndex, toIndex);
    });
  }, []);

  const saveImageOrder = useCallback(async () => {
    try {
      const res = await updateImageOrder({
        variables: {
          input: {
            newImageOrder: localImages.map((item, index) => ({
              artworkId: item.artworkId,
              archiveId: item.archiveId,
              order: index + 1,
            })),
          },
        },
      });
      const images = res?.data?.updateImageOrder.images;
      const success = res?.data?.updateImageOrder.success;

      if (success && images?.length > 0) {
        setImageList?.(images);
        setLocalImages(images);
      }
      return Boolean(success);
    } catch (err) {
      console.error("saveImageOrder", err);
      return false;
    }
  }, [updateImageOrder, localImages, setImageList]);

  const handleUpdateOrder = async e => {
    e.preventDefault();
    await saveImageOrder();
  };

  const handleRequestClose = useCallback(
    closeModal => {
      if (hasUnsavedReorder) {
        setShowUnsavedWarning(true);
      } else {
        closeModal();
      }
    },
    [hasUnsavedReorder],
  );

  const handleZoom = useCallback(
    direction =>
      setThumbnailScale(
        clampThumbnailScale(thumbnailScale + direction * THUMBNAIL_SCALE_STEP),
      ),
    [thumbnailScale, setThumbnailScale],
  );

  const handleExitAnyway = useCallback(closeModal => {
    setShowUnsavedWarning(false);
    closeModal();
  }, []);

  const handleSaveAndExit = useCallback(
    async closeModal => {
      const success = await saveImageOrder();
      if (success) {
        setShowUnsavedWarning(false);
        closeModal();
      }
    },
    [saveImageOrder],
  );

  const onMoveSuccess = () => {
    removeUnLinkedImages();
    setSuccessOpModal?.({ isOpen: true, message: "Images has been moved." });
    resetLocalState();
  };

  const modalProps = {
    scale: getModalScaleForFullscreen(fullscreen),
    // Don't let a backdrop/escape close bypass the unsaved-reorder warning.
    closeOnBackdrop: !hasUnsavedReorder,
    hideCloseButton: true,
    // Always start afresh on open: discard any prior unsaved reordering and
    // selection (e.g. left behind after the user chose "Exit anyway") and
    // start from the canonical image order.
    onOpen: resetLocalState,
    onClose: resetLocalState,
    // renderBody keeps Upload Image mounted. `body` is a new component on
    // every render, so the artwork refetch mid-upload tore the progress
    // dialog down.
    renderBody: ({ closeModal }) => {
      const thumbnailSize = getThumbnailSizeForScale(thumbnailScale);

      return (
        <div
          className={`relative flex w-full min-w-0 flex-col p-3 sm:p-4 lg:p-8 ${
            isFullscreen ? "min-h-0 flex-1" : ""
          }`}
        >
          {showUnsavedWarning && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/80 p-3">
              <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-4 shadow-xl lg:p-6">
                <div className="text-xl font-bold text-gray-900">
                  Unsaved changes
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  You have reordered images but haven&apos;t saved them. What
                  would you like to do?
                </p>
                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
                  <Button
                    action="default"
                    label="Cancel"
                    disabled={updateImageOrderLoading}
                    onClick={() => setShowUnsavedWarning(false)}
                  />
                  <Button
                    action="default"
                    label="Exit anyway"
                    disabled={updateImageOrderLoading}
                    onClick={() => handleExitAnyway(closeModal)}
                  />
                  <Button
                    className="flex items-center"
                    label={updateImageOrderLoading ? "Saving..." : "Save & exit"}
                    loading={updateImageOrderLoading}
                    disabled={updateImageOrderLoading}
                    onClick={() => handleSaveAndExit(closeModal)}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 lg:flex-nowrap">
            <button
              type="button"
              aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
              title={isFullscreen ? "Exit full screen" : "Enter full screen"}
              onClick={() =>
                setFullscreen(isFullscreen ? FULLSCREEN_OFF : FULLSCREEN_ON)
              }
              className="flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 lg:mr-3"
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
            <div className="min-w-0 flex-1 text-xl font-bold sm:text-2xl">
              Manage Artwork Images
            </div>
            <div className="order-last flex w-full flex-wrap gap-2 sm:gap-4 lg:order-none lg:w-auto lg:flex-nowrap">
              {hasPermission("CREATE_IMAGE") && (
                <UploadImage
                  mainCloseModal={closeModal}
                  artwork={artwork}
                  currentImages={propsImages}
                />
              )}
              {canUpdateOrder && (
                <Button
                  className="flex !min-w-28 items-center"
                  label="Update Order"
                  loading={updateImageOrderLoading}
                  disabled={updateImageOrderLoading || !enableUpdateOrderButton}
                  onClick={handleUpdateOrder}
                />
              )}
              {moveAble && (
                <MoveImage
                  selectedImages={selectedImages}
                  artworkId={propsImages[0]?.artworkId}
                  onSuccess={onMoveSuccess}
                  mainCloseModal={closeModal}
                />
              )}
              {hasPermission("DELETE_IMAGE") && deleteAble && (
                <DeleteImage
                  selectedImages={selectedImages}
                  artworkId={propsImages[0]?.artworkId}
                  onSuccess={onDeleteSuccess}
                />
              )}
            </div>
            <button
              type="button"
              aria-label="Close"
              title="Close"
              onClick={() => handleRequestClose(closeModal)}
              className="ml-auto flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 lg:ml-4"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div
            className={`mt-8 ${
              isFullscreen ? "flex min-h-0 flex-1 flex-col" : ""
            }`}
          >
            <div className="mb-4 flex flex-wrap items-center justify-end gap-3 text-gray-600">
              <button
                type="button"
                aria-label="Zoom out"
                title="Zoom out"
                disabled={thumbnailScale <= THUMBNAIL_SCALE_MIN}
                onClick={() => handleZoom(-1)}
                className="flex items-center justify-center rounded-md p-1 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <MagnifyingGlassMinusIcon className="h-5 w-5" />
              </button>
              <input
                type="range"
                aria-label="Thumbnail scale"
                min={THUMBNAIL_SCALE_MIN}
                max={THUMBNAIL_SCALE_MAX}
                step={THUMBNAIL_SCALE_STEP}
                value={thumbnailScale}
                onChange={event => setThumbnailScale(Number(event.target.value))}
                className="w-full max-w-48 cursor-pointer accent-black"
              />
              <button
                type="button"
                aria-label="Zoom in"
                title="Zoom in"
                disabled={thumbnailScale >= THUMBNAIL_SCALE_MAX}
                onClick={() => handleZoom(1)}
                className="flex items-center justify-center rounded-md p-1 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <MagnifyingGlassPlusIcon className="h-5 w-5" />
              </button>
            </div>
            <div
              className={`min-h-48 overflow-auto ${
                isFullscreen
                  ? "min-h-0 flex-1"
                  : "max-h-[55dvh] lg:h-[500px] lg:max-h-none"
              }`}
            >
              {localImages?.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={localImages.map(item => item.archiveId)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="flex flex-wrap gap-4">
                      {localImages?.map((item, index) => (
                        <SortableThumbnail
                          key={item.archiveId}
                          item={item}
                          index={index}
                          thumbnailSize={thumbnailSize}
                          isSelected={selectedImages.includes(item.archiveId)}
                          onSelect={handleSelectImage}
                          onUnselect={handleUnselectImage}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div
                  className={`text-md flex h-full w-full items-center justify-center text-gray-600`}
                >
                  NO IMAGES TO SHOW
                </div>
              )}
            </div>
            {localImages.length > 0 && (
              <div
                className={`mt-5 w-full text-right text-lg font-bold tracking-wide text-gray-600`}
              >
                {selectedImages.length} of {localImages.length}{" "}
                <button
                  type="button"
                  disabled={selectedImages.length === 0}
                  onClick={() => setSelectedImages([])}
                  className={`inline align-baseline ${
                    selectedImages.length > 0
                      ? "cursor-pointer text-blue-600 underline hover:text-blue-700"
                      : "cursor-default text-gray-600 no-underline"
                  }`}
                >
                  selected
                </button>
              </div>
            )}
          </div>
        </div>
      );
    },
  };

  return (
    <Modal {...modalProps}>
      <Button label="Manage Images" className={"!w-32"} />
    </Modal>
  );
};

export default ManageImage;
